import Dialog from '@jetbrains/ring-ui/components/dialog/dialog'
import {Header, Content} from '@jetbrains/ring-ui/components/island/island'
import Link from '@jetbrains/ring-ui/components/link/link'
import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import classNames from 'classnames'
import React from 'react'

import CertificateFetchForm from '../../CertificateInstallationForm/CertificateFetchForm/CertificateFetchForm'
import httpsConfigurationApi from '../../HttpsConfigurationPage.api'

import styles from './CertificateRenewal.css'

const EXPIRATION_THRESHOLD = 30

type Props = {
  daysBeforeExpiration: number
  fetchedFromAcme: boolean
}
function CertificateRenewal({daysBeforeExpiration, fetchedFromAcme}: Props) {
  const [displayDialog, setDisplayDialog] = React.useState(false)

  const [fetchCertificate, {isLoading}] =
    httpsConfigurationApi.endpoints.fetchCertificateFromAcme.useMutation()

  const shouldShowWarning = daysBeforeExpiration <= EXPIRATION_THRESHOLD
  const {data: pendingAcmeOrder} = httpsConfigurationApi.endpoints.getPendingAcmeOrder.useQuery(
    undefined,
    {skip: !fetchedFromAcme || !shouldShowWarning},
  )

  const onCloseAttempt = React.useCallback(() => setDisplayDialog(false), [])
  const handleRenewClick = React.useCallback(() => {
    if (pendingAcmeOrder != null) {
      setDisplayDialog(true)
      return
    }
    fetchCertificate(null)
      .unwrap()
      .then(() => setDisplayDialog(true))
  }, [pendingAcmeOrder, fetchCertificate])

  React.useEffect(() => {
    if (pendingAcmeOrder == null) {
      setDisplayDialog(false)
    }
  }, [pendingAcmeOrder])

  return shouldShowWarning ? (
    <>
      <div
        className={classNames(
          styles.expirationInfo,
          daysBeforeExpiration > 0 ? styles.warning : styles.expired,
          !fetchedFromAcme && styles.infoMargin,
        )}
      >
        <span>
          {daysBeforeExpiration > 0
            ? `The certificate will expire in ${
                daysBeforeExpiration > 1 ? `${daysBeforeExpiration} days` : 'less than a day'
              }.`
            : 'The certificate is expired.'}
        </span>
        {fetchedFromAcme && (
          <>
            <Link
              className={styles.renewalBtn}
              onClick={handleRenewClick}
              disabled={isLoading}
              pseudo
            >
              {'Renew'}
            </Link>
            {isLoading && <LoaderInline />}
            {displayDialog && (
              <Dialog
                contentClassName={styles.dialog}
                onCloseAttempt={onCloseAttempt}
                autoFocusFirst={false}
                showCloseButton
                show
              >
                <Header>{'Certificate Renewal'}</Header>
                <Content scrollableWrapperClassName={styles.dialogContent}>
                  <CertificateFetchForm />
                </Content>
              </Dialog>
            )}
          </>
        )}
      </div>
    </>
  ) : null
}

export default React.memo<Props>(CertificateRenewal)
